import { CardSize } from '@enter/core';
import { Fade } from '@mui/material';
import { ClaimVolumeChart } from 'components';
import * as d3 from 'd3';
import _ from 'lodash';
import { reaction } from 'mobx';
import { disposeOnUnmount, inject, observer } from 'mobx-react';
import { Granularity } from 'models';
import React from 'react';
import { Link } from 'react-router-dom';
import { IInjectedRootStore } from 'stores';
import { IChartPoint, IChartTimeline, Legend, summarizeTimeline, timelineLabels } from '../chart';
import * as S from './claim-volume-card.styles';

interface IClaimVolumeCardProps {
    children?: React.ReactNode;
    size?: CardSize;
}

interface IClaimVolumeCardState {
    granularity: Granularity;
}

@inject('rootStore')
@observer
export class ClaimVolumeCard extends React.Component<IClaimVolumeCardProps, IClaimVolumeCardState> {
    constructor(props: IClaimVolumeCardProps) {
        super(props);
        this.state = {
            granularity: Granularity.Monthly,
        };
    }
    public componentDidMount(): void {
        this.autoSelectGranularity(null);
        disposeOnUnmount(
            this,
            reaction(() => this.injected.rootStore.dateRangeStore.dateRange, this.autoSelectGranularity),
        );
    }
    public render(): React.ReactElement {
        return (
            <S.ClaimVolumeCard
                size={this.props.size || 'medium'}
                title="Claim Volume"
                selectedGranularity={this.state.granularity}
                onGranularitySelect={this.changeGranularity}
                info={this.renderInfo()}
                Caption={this.renderCaption()}
                Options={this.renderOptions()}
                Legend={this.renderLegend()}
                Link={this.renderLink()}>
                {this.renderChildren()}
            </S.ClaimVolumeCard>
        );
    }
    private get injected(): IInjectedRootStore {
        return this.props as IClaimVolumeCardProps & IInjectedRootStore;
    }
    private get timeline(): IChartTimeline | undefined {
        return summarizeTimeline(this.injected.rootStore.metricsStore.claimVolumeTimeline, this.state.granularity);
    }
    private readonly changeGranularity = (granularity: Granularity): void => {
        if (granularity !== this.state.granularity) {
            this.setState({
                granularity,
            });
        }
    };
    private readonly autoSelectGranularity = (d: any): void => {
        const { dateRangeStore } = this.injected.rootStore;
        if (!dateRangeStore.hasGranularityForDateRange(this.state.granularity)) {
            this.changeGranularity(dateRangeStore.findGranularityForDateRange());
        }
    };

    private readonly exportData = (format: string): (() => void) => {
        return (): void => {
            window.alert(`TODO: Export ClaimVolumes data as ${format}`);
        };
    };
    private readonly renderInfo = (): React.ReactElement => {
        return <span>Track the financial performance of your practice.</span>;
    };
    private readonly renderCaption = (): React.ReactElement | undefined => {
        if (this.timeline) {
            const fmt = d3.format('$,.2f');
            const sum = (arr: IChartPoint[]): number => _.sum(_.map(arr, 'amount'));

            // NB: [ volume, payer, patient ]
            const volume = sum(this.timeline.data[0]);
            const payer = sum(this.timeline.data[1]);

            if (volume || payer) {
                return (
                    <Fade in={true}>
                        <S.CaptionRow>
                            <S.CaptionCol>
                                <S.CaptionLabel>Total Claims Volume</S.CaptionLabel>
                                <S.CaptionValue>{fmt(volume)}</S.CaptionValue>
                            </S.CaptionCol>
                            <S.CaptionCol>
                                <S.CaptionLabel>Total Payer Paid</S.CaptionLabel>
                                <S.CaptionValue>{fmt(payer)}</S.CaptionValue>
                            </S.CaptionCol>
                        </S.CaptionRow>
                    </Fade>
                );
            }
        }
    };
    private readonly renderOptions = (): React.ReactElement | undefined => {
        return undefined;
    };
    private readonly renderLegend = (): React.ReactElement => {
        return (
            <Legend
                direction={this.props.size === 'large' ? 'vertical' : 'horizontal'}
                labels={timelineLabels.claimVolume}
            />
        );
    };
    private readonly renderLink = (): React.ReactElement => {
        return <Link to="/reports">Claims Report</Link>;
    };
    private readonly renderChildren = (): React.ReactElement => {
        const { dateRangeStore, metricsStore } = this.injected.rootStore;
        const [startDate, endDate] = dateRangeStore.dateRange;

        if (_.isError(metricsStore.claimVolumeTimeline)) {
            return <S.Message>No data is currently available.</S.Message>;
        } else if (dateRangeStore.hasGranularityForDateRange(this.state.granularity)) {
            return (
                <ClaimVolumeChart
                    granularity={this.state.granularity}
                    startDate={startDate}
                    endDate={endDate}
                    claimTypes={metricsStore.selectedClaimTypes}
                />
            );
        } else {
            return (
                <S.Message>
                    The current date range cannot be viewed with the {Granularity[this.state.granularity]} setting.
                </S.Message>
            );
        }
    };
}
